import React from 'react';
import { createPortal } from 'react-dom';
import { Minus, Plus, Sparkles, Users } from 'lucide-react';
import { formatRetryAfter } from '../api/aiOfferRecommendationsApi';

// Only these buckets can be targeted by a campaign (ACTIVE / FIRST_TIME are
// analytics-only — the offer normalizer refuses them).
const TARGETABLE_BUCKETS = [
  'DORMANT',
  'SLIPPING_AWAY',
  'AT_RISK',
  'DUE_SOON',
  'LOYAL_ON_TIME',
  'HIGH_SPEND',
  'OFFER_SENSITIVE'
];

/** Plan key for the untargeted "All customers" slice — shares the budget with buckets. */
const ALL_CUSTOMERS_KEY = 'ALL';

/** One generation run always produces this many offers — the plan must add up to it. */
export const GENERATION_OFFER_BUDGET = 5;

const formatNumber = (value) => new Intl.NumberFormat('en-IN').format(Number(value || 0));

/**
 * The single entry point for AI offer generation. One run yields exactly
 * {@link GENERATION_OFFER_BUDGET} offers, so the merchant first plans WHO they
 * are for by spreading the budget across "All customers" and/or retention
 * buckets — any mix is allowed, but the plan must total the full budget so a
 * run never silently wastes quota. Clicking the All-customers card pours the
 * unplanned remainder into it, keeping the old one-click smart mix.
 */
const AiGenerateTargetModal = ({
  open,
  onClose,
  onGenerate,
  buckets = [],
  generationQuota = null,
  busy = false
}) => {
  const [counts, setCounts] = React.useState({});

  // Fresh plan every time the picker opens.
  React.useEffect(() => {
    if (open) {
      setCounts({});
    }
  }, [open]);

  const targetableBuckets = React.useMemo(() => (
    (Array.isArray(buckets) ? buckets : [])
      .filter((bucket) => TARGETABLE_BUCKETS.includes(bucket?.bucket))
  ), [buckets]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  const quotaExhausted = Boolean(generationQuota && (generationQuota.remaining ?? 1) <= 0);
  const quotaResetText = quotaExhausted && generationQuota?.retryAtMs
    ? formatRetryAfter(Math.round((generationQuota.retryAtMs - Date.now()) / 1000))
    : null;
  const locked = busy || quotaExhausted;

  // Budget is offers-per-window: a partly-spent window (e.g. after a dead-hours
  // fill) can plan only the offers that are still left, never the full five.
  const effectiveBudget = generationQuota && Number.isFinite(generationQuota.remaining)
    ? Math.max(0, Math.min(GENERATION_OFFER_BUDGET, generationQuota.remaining))
    : GENERATION_OFFER_BUDGET;

  const plannedTotal = Object.values(counts).reduce((sum, value) => sum + (Number(value) || 0), 0);
  const budgetLeft = effectiveBudget - plannedTotal;
  const planFull = effectiveBudget > 0 && plannedTotal === effectiveBudget;
  const canProceed = !locked && planFull;
  const allCount = Number(counts[ALL_CUSTOMERS_KEY] || 0);

  const adjustCount = (key, delta) => {
    if (locked) {
      return;
    }
    setCounts((previous) => {
      const current = Number(previous[key] || 0);
      const next = Math.max(0, Math.min(effectiveBudget, current + delta));
      const totalOthers = Object.entries(previous)
        .filter(([otherKey]) => otherKey !== key)
        .reduce((sum, [, value]) => sum + (Number(value) || 0), 0);
      const capped = Math.min(next, effectiveBudget - totalOthers);
      const updated = { ...previous };
      if (capped <= 0) {
        delete updated[key];
      } else {
        updated[key] = capped;
      }
      return updated;
    });
  };

  // One-click smart mix: pour whatever is still unplanned into All customers.
  const fillRemainingIntoAll = () => {
    if (locked) {
      return;
    }
    setCounts((previous) => {
      const total = Object.values(previous).reduce((sum, value) => sum + (Number(value) || 0), 0);
      const left = effectiveBudget - total;
      if (left <= 0) {
        return previous;
      }
      return {
        ...previous,
        [ALL_CUSTOMERS_KEY]: Number(previous[ALL_CUSTOMERS_KEY] || 0) + left
      };
    });
  };

  const handleProceed = () => {
    if (!canProceed) {
      return;
    }
    const bucketAllocations = targetableBuckets
      .filter((bucket) => Number(counts[bucket.bucket] || 0) > 0)
      .map((bucket) => ({
        bucket: bucket.bucket,
        displayName: bucket.displayName || bucket.bucket,
        suggestedAction: bucket.suggestedAction || '',
        customerCount: bucket.count ?? null,
        offerCount: Number(counts[bucket.bucket])
      }));
    if (bucketAllocations.length === 0) {
      // Whole budget on All customers — keep the legacy smart-mix run.
      onGenerate?.({ type: 'all' });
      return;
    }
    const allocations = allCount > 0
      ? [
        {
          bucket: ALL_CUSTOMERS_KEY,
          displayName: 'All customers',
          suggestedAction: '',
          customerCount: null,
          offerCount: allCount
        },
        ...bucketAllocations
      ]
      : bucketAllocations;
    onGenerate?.({ type: 'buckets', allocations });
  };

  const allOnly = planFull && allCount === plannedTotal;
  const statusLine = plannedTotal === 0
    ? `Plan all ${effectiveBudget} offers: fill All customers, pick buckets, or mix both.`
    : !planFull
      ? `${plannedTotal} of ${effectiveBudget} offers planned — add ${budgetLeft} more to proceed.`
      : allOnly
        ? `${effectiveBudget} offers will be generated based on analytics of your whole customer base.`
        : `All ${effectiveBudget} offers planned. Ready to generate.`;

  // Once the budget is spent, rows outside the plan lock up entirely —
  // free a slot with "−" (or close) to change the mix.
  const allLockedOut = planFull && allCount === 0;

  return createPortal(
    <div
      className="zone-modal-overlay offers-builder-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div
        className="zone-modal ai-target-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Plan who these offers are for"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="zone-modal-header">
          <div>
            <h3>Who are these offers for?</h3>
            <p>{`This run creates ${effectiveBudget} offer${effectiveBudget === 1 ? '' : 's'} — split them however you like. Nothing goes live without your publish.`}</p>
          </div>
          <button className="btn ghost" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="ai-target-options">
          <div
            className={`ai-target-option ai-target-bucket-row${allCount > 0 ? ' selected' : ''}${allLockedOut ? ' muted' : ''}`}
            aria-disabled={allLockedOut || undefined}
          >
            <button
              type="button"
              className="ai-target-option-fill"
              onClick={fillRemainingIntoAll}
              disabled={locked || budgetLeft === 0}
              aria-label="All customers — use the remaining offers"
            >
              <span className="ai-target-option-icon all" aria-hidden="true">
                <Sparkles size={16} />
              </span>
              <span className="ai-target-option-main">
                <strong>All customers</strong>
                <small>
                  Smart mix for your whole store — tap to use the remaining budget here.
                </small>
              </span>
            </button>
            <span className="ai-target-stepper">
              <button
                type="button"
                className="ai-target-step-btn"
                aria-label="Fewer offers for All customers"
                onClick={() => adjustCount(ALL_CUSTOMERS_KEY, -1)}
                disabled={locked || allCount === 0}
              >
                <Minus size={13} />
              </button>
              <span className="ai-target-step-count" aria-live="polite">{allCount}</span>
              <button
                type="button"
                className="ai-target-step-btn"
                aria-label="More offers for All customers"
                onClick={() => adjustCount(ALL_CUSTOMERS_KEY, 1)}
                disabled={locked || budgetLeft === 0}
              >
                <Plus size={13} />
              </button>
            </span>
          </div>

          <p className="ai-target-divider">and / or target customer groups</p>

          {targetableBuckets.map((bucket) => {
            const count = Number(counts[bucket.bucket] || 0);
            const lockedOut = planFull && count === 0;
            return (
              <div
                key={bucket.bucket}
                className={`ai-target-option ai-target-bucket-row${count > 0 ? ' selected' : ''}${lockedOut ? ' muted' : ''}`}
                aria-disabled={lockedOut || undefined}
              >
                <span className="ai-target-option-icon" aria-hidden="true">
                  <Users size={16} />
                </span>
                <span className="ai-target-option-main">
                  <strong>{bucket.displayName || bucket.bucket}</strong>
                  <small>
                    {formatNumber(bucket.count)} customer{Number(bucket.count) === 1 ? '' : 's'}
                    {bucket.suggestedAction ? ` · ${bucket.suggestedAction}` : ''}
                  </small>
                </span>
                <span className="ai-target-stepper">
                  <button
                    type="button"
                    className="ai-target-step-btn"
                    aria-label={`Fewer offers for ${bucket.displayName || bucket.bucket}`}
                    onClick={() => adjustCount(bucket.bucket, -1)}
                    disabled={locked || count === 0}
                  >
                    <Minus size={13} />
                  </button>
                  <span className="ai-target-step-count" aria-live="polite">{count}</span>
                  <button
                    type="button"
                    className="ai-target-step-btn"
                    aria-label={`More offers for ${bucket.displayName || bucket.bucket}`}
                    onClick={() => adjustCount(bucket.bucket, 1)}
                    disabled={locked || budgetLeft === 0}
                  >
                    <Plus size={13} />
                  </button>
                </span>
              </div>
            );
          })}
        </div>

        <div className="ai-target-footer">
          <p className={`ai-target-status${canProceed ? ' ready' : ''}`}>
            {quotaExhausted
              ? `Generation limit reached${quotaResetText ? ` · resets in ${quotaResetText}` : ''}`
              : statusLine}
          </p>
          <button
            className="btn primary"
            type="button"
            onClick={handleProceed}
            disabled={!canProceed}
          >
            <Sparkles size={15} />
            {`Generate ${effectiveBudget} offer${effectiveBudget === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AiGenerateTargetModal;
